import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { nutritionAPI, type FoodBrand } from "@/lib/nutrition-api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

export const FoodBrands = () => {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [brandName, setBrandName] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const { data: brands, isLoading } = useQuery({
    queryKey: ["food-brands"],
    queryFn: () => nutritionAPI.foodBrands.list(),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => nutritionAPI.foodBrands.create({ name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["food-brands"] });
      toast.success("Food brand added successfully");
      setBrandName("");
      setIsDialogOpen(false);
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to add food brand");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!brandName.trim()) {
      toast.error("Please enter a brand name");
      return;
    }
    createMutation.mutate(brandName);
  };

  const handleSearch = () => {
    setSearchQuery(searchInput);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const filteredBrands = useMemo(() => {
    if (!brands) return [];
    if (!searchQuery.trim()) return brands;
    const q = searchQuery.toLowerCase();
    return brands.filter((b) => b.name.toLowerCase().includes(q));
  }, [brands, searchQuery]);

  const totalCount = brands?.length ?? 0;

  return (
    <div className="h-full flex flex-col gap-4">
      <Card className="flex-1 flex flex-col min-h-0">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="z-h2">Food Brands</CardTitle>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2">
                <Plus className="h-4 w-4" />
                Add Brand
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Food Brand</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="brandName">Brand Name</Label>
                  <Input
                    id="brandName"
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                    placeholder="Enter brand name"
                    disabled={createMutation.isPending}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    disabled={createMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createMutation.isPending}>
                    {createMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Add Brand
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col overflow-hidden">
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search food brands by name..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className="pl-10"
              />
            </div>
            <Button onClick={handleSearch} disabled={isLoading}>
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Search"
              )}
            </Button>
          </div>

          <div className="mb-3 z-caption text-muted-foreground">
            Showing {filteredBrands.length} of {totalCount} brand
            {totalCount !== 1 ? "s" : ""}
            {searchQuery && ` for "${searchQuery}"`}
          </div>

          <div className="border rounded-lg flex-1 overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  <TableHead className="w-[80px] z-eyebrow text-muted-foreground">
                    ID
                  </TableHead>
                  <TableHead className="z-eyebrow text-muted-foreground">
                    Brand Name
                  </TableHead>
                  <TableHead className="z-eyebrow text-muted-foreground">
                    Created At
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                      <p className="z-caption text-muted-foreground mt-2">
                        Loading brands...
                      </p>
                    </TableCell>
                  </TableRow>
                ) : filteredBrands.length > 0 ? (
                  filteredBrands.map((brand: FoodBrand) => (
                    <TableRow key={brand.id} className="hover:bg-muted/30">
                      <TableCell className="z-caption text-muted-foreground tabular-nums">
                        {brand.id}
                      </TableCell>
                      <TableCell className="z-body">{brand.name}</TableCell>
                      <TableCell className="z-caption text-muted-foreground">
                        {new Date(brand.created_at).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          }
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="text-center py-8 text-muted-foreground"
                    >
                      {searchQuery
                        ? "No food brands match your search."
                        : "No food brands found. Add one to get started."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
